import { Link, Navigate, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle2,
  Code2,
  RefreshCcw,
  Search,
  Smartphone,
  Navigation,
  Wifi,
  Globe,
  Link2,
  GraduationCap,
  LifeBuoy,
  ClipboardList,
  ShieldCheck,
  Workflow,
  Rocket,
} from "lucide-react";

const servicePages = {
  "modules-odoo-sur-mesure": {
    category: "Odoo & ERP",
    icon: Code2,
    title: "Modules Odoo sur mesure",
    tagline: "Un Odoo qui épouse vos méthodes de travail, sans contournements ni doubles saisies.",
    description: "Nous traduisons vos règles métier en modules Odoo robustes : écrans, workflows, automatisations, rapports et droits d’accès adaptés à votre organisation.",
    capabilities: ["Ateliers fonctionnels et spécifications", "Modèles, vues et workflows Odoo", "Rapports PDF, tableaux de bord et KPI", "Automatisations et actions planifiées", "Gestion fine des rôles et permissions", "Tests, documentation et mise en production"],
    useCases: ["Gestion de chantier ou d’intervention", "Circuit de validation des achats", "Facturation métier spécifique", "Portail client ou fournisseur"],
    deliverables: ["Cahier de spécifications validé", "Module versionné et documenté", "Environnement de recette", "Formation et guide utilisateur"],
  },
  "migration-odoo": {
    category: "Odoo & ERP",
    icon: RefreshCcw,
    title: "Migration Odoo",
    tagline: "Passez à une version récente d’Odoo sans perdre vos données ni bloquer l’activité.",
    description: "Nous auditons l’existant, préparons les scripts de reprise, adaptons les modules spécifiques et organisons une bascule maîtrisée avec plan de retour.",
    capabilities: ["Audit de la version et des modules", "Nettoyage et cartographie des données", "Portage du code personnalisé", "Migrations à blanc et contrôles", "Tests de non-régression", "Bascule, sauvegarde et suivi renforcé"],
    useCases: ["Odoo ancien devenu difficile à maintenir", "Passage Community vers Enterprise", "Fusion de plusieurs bases", "Reprise depuis un autre ERP"],
    deliverables: ["Rapport d’audit et risques", "Plan de migration détaillé", "Base de préproduction", "Procès-verbal de contrôles"],
  },
  "audit-conseil-odoo": {
    category: "Odoo & ERP",
    icon: Search,
    title: "Audit & conseil Odoo",
    tagline: "Comprendre ce qui ralentit votre Odoo et décider quoi corriger en priorité.",
    description: "L’audit couvre l’usage, la performance, la sécurité, la qualité du code et l’architecture. Vous obtenez un plan d’action concret, chiffré et priorisé.",
    capabilities: ["Entretiens avec les utilisateurs", "Analyse des performances", "Revue des modules spécifiques", "Contrôle des accès et de la sécurité", "Analyse des flux et doubles saisies", "Feuille de route budgétée"],
    useCases: ["Lenteurs ou erreurs récurrentes", "Projet Odoo inachevé", "Coûts de maintenance trop élevés", "Préparation d’une évolution importante"],
    deliverables: ["Rapport d’audit illustré", "Matrice risques / priorités", "Gains rapides identifiés", "Plan d’action à 3, 6 et 12 mois"],
  },
  "applications-ios-android": {
    category: "Applications mobiles",
    icon: Smartphone,
    title: "Applications iOS & Android",
    tagline: "Une application rapide et moderne, connectée à vos données Odoo.",
    description: "Nous concevons l’expérience, développons l’application et prenons en charge sa connexion sécurisée à Odoo ainsi que sa publication sur les stores.",
    capabilities: ["Cadrage produit et parcours utilisateurs", "Design UX/UI et prototype interactif", "Développement iOS et Android", "Connexion sécurisée aux API Odoo", "Notifications, paiement et géolocalisation", "Publication App Store et Google Play"],
    useCases: ["Espace client en libre-service", "Réservation et fidélité", "Catalogue et commande mobile", "Suivi de dossier ou de livraison"],
    deliverables: ["Prototype testable", "Application et code source", "API et documentation technique", "Fiches et versions pour les stores"],
  },
  "applications-metier-terrain": {
    category: "Applications mobiles",
    icon: Navigation,
    title: "Applications métier & terrain",
    tagline: "Les bonnes informations au bon moment pour vos équipes mobiles.",
    description: "Techniciens, commerciaux, livreurs ou conducteurs de travaux disposent d’une interface simple pour consulter, saisir et synchroniser leurs opérations.",
    capabilities: ["Planning et fiches d’intervention", "Photos, documents et commentaires", "Scan de codes-barres ou QR codes", "Signature et compte rendu client", "GPS, tournées et horodatage", "Synchronisation automatique avec Odoo"],
    useCases: ["Maintenance d’équipements", "États des lieux", "Livraisons et preuves de passage", "Suivi de chantier"],
    deliverables: ["Parcours terrain optimisé", "Application mobile sécurisée", "Tableaux de bord superviseur", "Formation des équipes"],
  },
  "pwa-mode-hors-ligne": {
    category: "Applications mobiles",
    icon: Wifi,
    title: "PWA & mode hors ligne",
    tagline: "Continuez à travailler même lorsque le réseau est faible ou absent.",
    description: "Les données nécessaires sont disponibles localement puis synchronisées avec Odoo au retour de la connexion, avec gestion des conflits et traçabilité.",
    capabilities: ["Application web installable", "Cache intelligent des données", "Saisie et consultation hors connexion", "File de synchronisation sécurisée", "Gestion des conflits de données", "Mises à jour sans passage en store"],
    useCases: ["Chantiers peu couverts", "Entrepôts et zones techniques", "Tournées en déplacement", "Formulaires et inventaires terrain"],
    deliverables: ["PWA responsive et installable", "Moteur de synchronisation", "Scénarios de reprise testés", "Guide de fonctionnement hors ligne"],
  },
  "sites-portails-odoo": {
    category: "Web & connexions",
    icon: Globe,
    title: "Sites & portails Odoo",
    tagline: "Un site rapide et élégant, directement relié à votre back-office.",
    description: "Catalogue, stock, demandes, commandes et espace client restent synchronisés avec Odoo, sans ressaisie ni connecteurs fragiles.",
    capabilities: ["Site vitrine et pages d’atterrissage", "E-commerce connecté au stock", "Portail client ou fournisseur", "Paiement, devis et réservation", "SEO technique et mesure d’audience", "Hébergement, sécurité et maintenance"],
    useCases: ["Site institutionnel", "Boutique B2C ou B2B", "Espace documentaire client", "Prise de rendez-vous ou réservation"],
    deliverables: ["Maquettes responsive", "Site administrable", "Connexion aux données Odoo", "Formation à la publication"],
  },
  "integrations-api": {
    category: "Web & connexions",
    icon: Link2,
    title: "Intégrations API",
    tagline: "Faites circuler les données entre Odoo et vos outils, automatiquement et sûrement.",
    description: "Nous réalisons les connecteurs, règles de synchronisation, contrôles et alertes nécessaires pour fiabiliser vos échanges de données.",
    capabilities: ["API REST, JSON-RPC et webhooks", "Synchronisation bidirectionnelle", "Import, export et transformation", "Files d’attente et reprises sur erreur", "Journalisation et alertes", "Documentation et tests automatisés"],
    useCases: ["Marketplace et e-commerce", "Banque et comptabilité", "Transport et logistique", "CRM, signature ou paiement"],
    deliverables: ["Cartographie des données", "Connecteur documenté", "Tableau de suivi des échanges", "Procédure de reprise sur incident"],
  },
  "iot-equipements-connectes": {
    category: "Web & connexions",
    icon: Wifi,
    title: "IoT & équipements connectés",
    tagline: "Transformez les données de vos machines en alertes et actions métier.",
    description: "Capteurs, véhicules ou équipements remontent leurs informations vers Odoo pour déclencher maintenance, alertes et tableaux de bord.",
    capabilities: ["Collecte de télémétrie", "Connexion MQTT ou API", "Alertes selon des seuils métier", "Historique et tableaux de bord", "Maintenance préventive", "Association aux équipements Odoo"],
    useCases: ["Suivi de flotte et kilométrage", "Température ou consommation", "État et disponibilité de machines", "Déclenchement automatique d’interventions"],
    deliverables: ["Architecture de collecte", "Connecteur équipements / Odoo", "Règles d’alerte", "Tableaux de bord opérationnels"],
  },
  "formation-equipes": {
    category: "Accompagnement",
    icon: GraduationCap,
    title: "Formation des équipes",
    tagline: "Rendre chaque utilisateur autonome sur les tâches qui comptent vraiment.",
    description: "Les formations sont construites à partir de vos données, de vos rôles et de vos scénarios réels, avec exercices et supports réutilisables.",
    capabilities: ["Analyse des profils utilisateurs", "Parcours par métier", "Sessions en présentiel ou à distance", "Exercices sur une base de formation", "Supports écrits et vidéos", "Évaluation et suivi d’adoption"],
    useCases: ["Démarrage d’un nouvel Odoo", "Arrivée de nouveaux collaborateurs", "Déploiement d’un module", "Remise à niveau des pratiques"],
    deliverables: ["Programme pédagogique", "Supports personnalisés", "Enregistrements si prévus", "Bilan et recommandations"],
  },
  "maintenance-support": {
    category: "Accompagnement",
    icon: LifeBuoy,
    title: "Maintenance & support",
    tagline: "Un interlocuteur disponible pour sécuriser et faire évoluer votre environnement.",
    description: "Nous corrigeons les incidents, surveillons les points sensibles et organisons les évolutions dans un cadre clair avec priorités et suivi.",
    capabilities: ["Support utilisateurs et administrateurs", "Correction des anomalies", "Maintenance préventive", "Mises à jour de sécurité", "Suivi des performances", "Petites évolutions planifiées"],
    useCases: ["Absence d’équipe technique interne", "Reprise d’une application existante", "Activité nécessitant de la réactivité", "Besoin de budget mensuel maîtrisé"],
    deliverables: ["Canal de support dédié", "Suivi des demandes et priorités", "Compte rendu périodique", "Plan d’amélioration continue"],
  },
  "cadrage-projet": {
    category: "Accompagnement",
    icon: ClipboardList,
    title: "Cadrage de projet",
    tagline: "Transformer une idée en projet clair, estimable et réalisable.",
    description: "Avant de développer, nous clarifions les objectifs, utilisateurs, parcours, données, contraintes et critères de réussite afin de réduire les risques.",
    capabilities: ["Atelier de découverte", "Cartographie des processus", "Personas et parcours clés", "Périmètre MVP et priorités", "Choix d’architecture", "Budget, planning et risques"],
    useCases: ["Nouvelle application métier", "Projet mobile connecté à Odoo", "Refonte d’un outil existant", "Automatisation d’un processus manuel"],
    deliverables: ["Note de cadrage", "Backlog priorisé", "Architecture fonctionnelle", "Estimation et feuille de route"],
  },
};

export default function ServiceDetailPage() {
  const { serviceSlug } = useParams();
  const service = servicePages[serviceSlug];

  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;

  return (
    <div className="min-h-screen bg-white" data-testid="service-detail-page">
      <Navbar />

      <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50" />
        <div className="absolute -top-40 right-0 w-[520px] h-[520px] rounded-full bg-indigo-100/60 blur-3xl" />
        <div className="nova-container relative z-10">
          <div className="max-w-4xl">
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 mb-7">
              ← Tous les services
            </Link>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Icon className="w-6 h-6 text-indigo-600" />
              </span>
              <span className="text-sm font-semibold uppercase tracking-wider text-indigo-600">{service.category}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">{service.title}</h1>
            <p className="text-xl md:text-2xl font-medium text-slate-700 max-w-3xl mb-5">{service.tagline}</p>
            <p className="text-lg leading-relaxed text-slate-600 max-w-3xl mb-9">{service.description}</p>
            <Link to="/onboarding">
              <Button className="nova-btn-primary text-base px-8 py-4 h-auto">
                Parler de mon besoin <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="nova-section bg-white">
        <div className="nova-container">
          <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-14">
            <div>
              <span className="text-sm font-semibold uppercase tracking-wider text-indigo-600">Ce que nous réalisons</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-5">Une prestation complète, de l’idée à l’usage</h2>
              <p className="text-slate-600 leading-relaxed">Chaque projet est cadré selon vos priorités. Vous gardez une vision claire du périmètre, des décisions et de l’avancement.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {service.capabilities.map((capability) => (
                <div key={capability} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-5">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                  <span className="font-medium text-slate-800">{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="nova-section bg-slate-950 text-white">
        <div className="nova-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-sm font-semibold uppercase tracking-wider text-indigo-300">Exemples d’utilisation</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3">Des solutions pensées pour le réel</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.useCases.map((useCase, index) => (
              <div key={useCase} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <span className="text-3xl font-bold text-indigo-400/40">0{index + 1}</span>
                <h3 className="font-semibold text-lg mt-5">{useCase}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nova-section bg-slate-50">
        <div className="nova-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold uppercase tracking-wider text-indigo-600">Livrables</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-5">Vous savez exactement ce qui vous est remis</h2>
              <div className="space-y-3 mt-7">
                {service.deliverables.map((deliverable) => (
                  <div key={deliverable} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 shrink-0" />
                    <span className="text-slate-700">{deliverable}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-4">
              {[
                [Workflow, "Cadrage partagé", "Objectifs, périmètre et priorités validés avant de produire."],
                [ShieldCheck, "Qualité contrôlée", "Sécurité, tests et recette intégrés à la réalisation."],
                [Rocket, "Mise en service accompagnée", "Déploiement, formation et suivi après le lancement."],
              ].map(([StepIcon, title, text]) => (
                <div key={title} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <span className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                    <StepIcon className="w-5 h-5 text-indigo-600" />
                  </span>
                  <div><h3 className="font-bold text-slate-900">{title}</h3><p className="text-sm text-slate-600 mt-1">{text}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="nova-section bg-indigo-600 text-center">
        <div className="nova-container">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Parlons de votre projet</h2>
          <p className="text-indigo-100 max-w-2xl mx-auto mb-9">Décrivez votre besoin en quelques minutes. Nous vous recontactons avec une première lecture du projet et les prochaines étapes utiles.</p>
          <Link to="/onboarding"><Button className="bg-white text-indigo-600 hover:bg-indigo-50 rounded-full px-9 py-4 h-auto font-semibold">Démarrer le cadrage <ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
